import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, EyeOffIcon, SearchIcon, ShieldIcon, UserIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Field, Input, Select, Textarea } from '../../components/ui/Field';
import { EmptyState } from '../../components/ui/EmptyState';
import { Modal } from '../../components/ui/Modal';
import { PermissionGate } from '../../components/admin/PermissionGate';
import { useGovernance } from '../../contexts/GovernanceContext';
import { STATE_LABEL, STATE_MEANING, adminUsers as seedUsers, type AccountState, type AdminUser } from '../../data/users';
const TABS = ['Accounts', 'Needs attention', 'Standing policy'] as const;
const STATE_TONE: Record<AccountState, 'ok' | 'warn' | 'danger' | 'neutral'> = {
  active: 'ok',
  pending_verification: 'warn',
  restricted: 'warn',
  suspended: 'danger',
  closed: 'neutral'
};

/** Actions that change standing. Each one demands a reason, in the person's words. */
const ACTIONS: {
  to: AccountState;
  label: string;
  blurb: string;
}[] = [{
  to: 'restricted',
  label: 'Restrict',
  blurb: 'Pause specific capabilities. They keep access and can respond.'
}, {
  to: 'suspended',
  label: 'Suspend',
  blurb: 'Prevent sign-in. Every record is preserved.'
}, {
  to: 'active',
  label: 'Restore',
  blurb: 'Return the account to full use of what its roles authorise.'
}];

/**
 * Account administration.
 *
 * An administrator can change what an account may do; they cannot read what the
 * person wrote. Every state change is a decision with a named actor and a stated
 * reason, and it lands in the audit log the same way whether it was allowed or
 * refused.
 */
export function UsersControl() {
  const {
    logAction
  } = useGovernance();
  const [tab, setTab] = useState<(typeof TABS)[number]>(TABS[0]);
  const [users, setUsers] = useState<AdminUser[]>(seedUsers);
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<'all' | AccountState>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [pending, setPending] = useState<{
    user: AdminUser;
    to: AccountState;
  } | null>(null);
  const [reason, setReason] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter(user => {
      if (stateFilter !== 'all' && user.state !== stateFilter) return false;
      if (q.length === 0) return true;
      return user.username.toLowerCase().includes(q) || user.displayName.toLowerCase().includes(q) || user.id.toLowerCase().includes(q);
    });
  }, [users, query, stateFilter]);
  const attention = users.filter(user => user.openReports > 0 || user.flags.length > 0 || user.state === 'pending_verification');
  function apply() {
    if (!pending || reason.trim().length < 8) return;
    const {
      user,
      to
    } = pending;
    setUsers(current => current.map(entry => entry.id === user.id ? {
      ...entry,
      state: to,
      stateReason: to === 'active' ? null : reason.trim(),
      history: [{
        id: `act-${Date.now()}`,
        action: `${STATE_LABEL[to]} applied`,
        actor: 'You',
        at: 'Just now',
        reason: reason.trim()
      }, ...entry.history]
    } : entry));
    logAction({
      action: `account.${to}`,
      resource: 'Account',
      resourceId: user.id,
      before: STATE_LABEL[user.state],
      after: STATE_LABEL[to],
      reason: reason.trim()
    });
    setPending(null);
    setReason('');
  }
  const open = users.find(user => user.id === openId) ?? null;
  return <div className="space-y-6" data-id="element-7248" data-mp="f84n1adstql8">
      <PageHeader eyebrow="Administration" title="Users and accounts" description="One account per person, carrying every role they hold. Changing standing changes what an account may do — never what it has already recorded." data-id="element-7249" data-mp="f732mfaw7rqu" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="User administration sections" data-id="element-7250" data-mp="b6j4p9ihwk3n" />

      {tab === 'Accounts' && <PermissionGate permission="users.view" label="Account directory" data-id="element-7251" data-mp="yxwy7isdulq7">
          <div className="space-y-5" data-id="element-7252" data-mp="12yqky8u39uhi">
            <Panel bodyClassName="p-5 sm:p-6" data-id="element-7253" data-mp="12xpc8b0jpk2y">
              <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_220px]" data-id="element-7254" data-mp="p728u5jub60j">
                <Field label="Search by name, @handle or account id" htmlFor="user-search" data-id="element-7255" data-mp="p4zl5s65toyz">
                  <Input id="user-search" value={query} onChange={event => setQuery(event.target.value)} placeholder="amarachi, CAMP-8FD2…" data-id="element-7256" data-mp="u3kcf23foyba" />
                </Field>
                <Field label="Standing" htmlFor="user-state" data-id="element-7257" data-mp="1vzoys1d0g7q0">
                  <Select id="user-state" value={stateFilter} onChange={event => setStateFilter(event.target.value as 'all' | AccountState)} data-id="element-7258" data-mp="1rzy2xj8mn1em">
                    <option value="all" data-id="element-7259" data-mp="20tt64lq6uhrm">All</option>
                    {(Object.keys(STATE_LABEL) as AccountState[]).map(state => <option key={state} value={state} data-id="element-7260" data-mp="1912763h4eeyo">
                        {STATE_LABEL[state]}
                      </option>)}
                  </Select>
                </Field>
              </div>
              <p className="mt-3 text-body-sm text-ink-muted" data-id="element-7261" data-mp="1i0gyt690wvkr">
                {filtered.length} of {users.length} accounts. Search matches the account id because
                the handle can change — the id cannot.
              </p>
            </Panel>

            {filtered.length === 0 ? <Panel data-id="element-7262" data-mp="31g1x90o2tmwo">
                <EmptyState icon={<SearchIcon className="h-5 w-5" data-id="element-7264" data-mp="35iotrevozw07" />} title="No accounts match" description="Nothing here matches that search and filter. Widen either one." action={<Button variant="secondary" onClick={() => {
            setQuery('');
            setStateFilter('all');
          }} data-id="element-7265" data-mp="3fh4ixcb5sv72">
                      Clear filters
                    </Button>} data-id="element-7263" data-mp="31gtro85xteld" />
              </Panel> : <Panel bodyClassName="p-0" data-id="element-7266" data-mp="2wi0ekgukbplq">
                <ul className="divide-y divide-line" data-id="element-7267" data-mp="2wis9rqk8zlbh">
                  {filtered.map(user => <li key={user.id} className="px-5 py-4 sm:px-6" data-id="element-7268" data-mp="1z1tjidpwijyi">
                      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3" data-id="element-7269" data-mp="24161ol5tv2ur">
                        <div className="min-w-0" data-id="element-7270" data-mp="1wiq3q6786hu6">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5" data-id="element-7271" data-mp="1wheys321n41h">
                            <p className="text-body-lg font-semibold text-ink" data-id="element-7272" data-mp="1rjvbkdndbgxe">
                              {user.displayName}
                            </p>
                            <Badge tone={STATE_TONE[user.state]} data-id="element-7273" data-mp="1hlpbm1l4gkv6">{STATE_LABEL[user.state]}</Badge>
                            {user.openReports > 0 && <Badge tone="warn" data-id="element-7274" data-mp="1dlf2wh5y3gcm">{user.openReports} open reports</Badge>}
                          </div>
                          <p className="cz-num mt-1 text-body-sm text-ink-muted" data-id="element-7275" data-mp="13sptrsxvaekg">
                            @{user.username} · {user.id}
                          </p>
                          <p className="mt-1 text-body-sm text-ink-muted" data-id="element-7276" data-mp="tvbpsst4ri91">
                            {user.roles.join(', ')}
                            {user.institution ? ` · ${user.institution}` : ''} · last seen{' '}
                            {user.lastSeen}
                          </p>
                        </div>
                        <Button size="sm" variant="secondary" onClick={() => setOpenId(user.id === openId ? null : user.id)} data-id="element-7277" data-mp="2ipsmjuhif6n1">
                          {user.id === openId ? 'Close' : 'Open account'}
                        </Button>
                      </div>
                    </li>)}
                </ul>
              </Panel>}

            {open && <AccountDetail user={open} onAct={to => setPending({
          user: open,
          to
        })} data-id="element-7278" data-mp="1l1sax46oks25" />}
          </div>
        </PermissionGate>}

      {tab === 'Needs attention' && <PermissionGate permission="users.view" label="Accounts needing attention" data-id="element-7279" data-mp="1pyr2beyymkwn">
          <Panel title={`${attention.length} accounts need a decision`} description="A flag is an observation for a person to weigh, not a finding. Nothing here has been acted on automatically." bodyClassName="p-0" data-id="element-7280" data-mp="1c1vq5dnewdcd">
            <ul className="divide-y divide-line" data-id="element-7281" data-mp="sg299xw30w4k">
              {attention.map(user => <li key={user.id} className="px-5 py-4 sm:px-6" data-id="element-7282" data-mp="16fboau8jxk6y">
                  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-7283" data-mp="16cp7s53onucl">
                    <div className="min-w-0" data-id="element-7284" data-mp="11ibgads567d7">
                      <p className="text-body font-semibold text-ink" data-id="element-7285" data-mp="1gc7nuagh2vzd">
                        {user.displayName}{' '}
                        <span className="cz-num font-normal text-ink-muted" data-id="element-7286" data-mp="1bdmxr3uxpson">@{user.username}</span>
                      </p>
                      <ul className="mt-2 space-y-1.5" data-id="element-7287" data-mp="1p7nrfj714pi5">
                        {user.state === 'pending_verification' && <li className="text-body-sm text-ink" data-id="element-7288" data-mp="1z3h7hcjyj14o">
                            Awaiting verification — {user.identityDocuments.length} documents
                            submitted.
                          </li>}
                        {user.flags.map(flag => <li key={flag.id} className="text-body-sm text-ink" data-id="element-7289" data-mp="3dg11odrxkodf">
                            <span className="font-medium" data-id="element-7290" data-mp="39ez3m09r2id1">{flag.label}</span>{' '}
                            <Badge plain data-id="element-7291" data-mp="39cuw78cn7180">{flag.automated ? 'Automated signal' : 'Human report'}</Badge>
                            <span className="mt-0.5 block text-ink-muted" data-id="element-7292" data-mp="34e1jhi56je8w">{flag.detail}</span>
                          </li>)}
                      </ul>
                    </div>
                    <Button size="sm" variant="secondary" onClick={() => {
                setTab('Accounts');
                setOpenId(user.id);
              }} data-id="element-7293" data-mp="xbbixq1er4k">
                      Review
                    </Button>
                  </div>
                </li>)}
            </ul>
          </Panel>
        </PermissionGate>}

      {tab === 'Standing policy' && <div className="grid gap-5 lg:grid-cols-2" data-id="element-7294" data-mp="21lfjo48h0c6b">
          <Panel title="What each standing means" bodyClassName="p-0" data-id="element-7295" data-mp="26neolijuzh5d">
            <ul className="divide-y divide-line" data-id="element-7296" data-mp="2blpvplirrdjw">
              {(Object.keys(STATE_LABEL) as AccountState[]).map(state => <li key={state} className="px-5 py-4 sm:px-6" data-id="element-7297" data-mp="2gcy90sn7zj2c">
                  <Badge tone={STATE_TONE[state]} data-id="element-7298" data-mp="2ke08nal1tx4u">{STATE_LABEL[state]}</Badge>
                  <p className="mt-2 max-w-[68ch] text-body-sm text-ink" data-id="element-7299" data-mp="2kg4almze0m2h">{STATE_MEANING[state]}</p>
                </li>)}
            </ul>
          </Panel>

          <div className="space-y-5" data-id="element-7300" data-mp="2uch2qgfaarv4">
            <Panel title="What administration cannot reach" data-id="element-7301" data-mp="2zb0q8l9j0bpl">
              <ul className="space-y-2" data-id="element-7302" data-mp="2zaioj943h5aa">
                {['Private notes, diary entries and drafts', 'Direct messages between people', 'Wellbeing and counselling activity', 'Balances, ledger entries and financial identifiers', 'Academic results and transcripts'].map(item => <li key={item} className="flex items-start gap-2 text-body-sm text-ink" data-id="element-7303" data-mp="ryrzu4vagpnq">
                    <EyeOffIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-7304" data-mp="16v9bkx7e46at" />
                    {item}
                  </li>)}
              </ul>
              <p className="mt-3 max-w-[64ch] border-t border-line pt-3 text-caption text-ink-subtle" data-id="element-7305" data-mp="1fue7rwvhbnjt">
                These are absent from the account record itself, not hidden by a screen. No role,
                including global super administrator, reaches them from here.
              </p>
            </Panel>

            <Panel title="Rules that bind every decision" data-id="element-7306" data-mp="1kn7aygbaunts">
              <ul className="space-y-2.5" data-id="element-7307" data-mp="1yle11aa0bjiw">
                {['A state change without a stated reason is refused. The reason is shown to the person affected.', 'Nothing is deleted. Suspension and closure preserve every record, including anything owed to the person.', 'An automated signal never changes standing on its own.', 'An account can always appeal, and an appeal creates a new case rather than editing the old decision.'].map(rule => <li key={rule} className="flex items-start gap-2 text-body-sm text-ink" data-id="element-7308" data-mp="295g22sd31dj4">
                    <ShieldIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-7309" data-mp="1ugtnwtz6yrth" />
                    {rule}
                  </li>)}
              </ul>
            </Panel>
          </div>
        </div>}

      <Modal open={pending !== null} onClose={() => {
      setPending(null);
      setReason('');
    }} title={pending ? `${STATE_LABEL[pending.to]} ${pending.user.displayName}?` : ''} description="The reason is recorded against the account, shown to the person and written to the audit log." data-id="element-7310" data-mp="1lf3qn7imoyns">
        {pending && <>
            <p className="max-w-[64ch] text-body-sm text-ink" data-id="element-7311" data-mp="2iuq7uqqq70ra">{STATE_MEANING[pending.to]}</p>
            <div className="mt-4" data-id="element-7312" data-mp="2eug1hcz0u8bw">
              <Field label="Reason" htmlFor="standing-reason" hint="Write it for the person receiving it, not for the log." data-id="element-7313" data-mp="2a37suj5imqzs">
                <Textarea id="standing-reason" rows={4} value={reason} onChange={event => setReason(event.target.value)} placeholder="What happened, and what they can do about it." data-id="element-7314" data-mp="252ac8b05m50e" />
              </Field>
            </div>
            <div className="mt-4 flex flex-wrap gap-2" data-id="element-7315" data-mp="1m5scp7f2d0tl">
              <Button variant="primary" onClick={apply} disabled={reason.trim().length < 8} data-id="element-7316" data-mp="1m7bw36w0nrzl">
                {STATE_LABEL[pending.to]} this account
              </Button>
              <Button variant="ghost" onClick={() => {
            setPending(null);
            setReason('');
          }} data-id="element-7317" data-mp="3gkl6d0wdzhiw">
                Cancel
              </Button>
            </div>
            {reason.trim().length < 8 && <p className="mt-2 text-body-sm text-ink-muted" data-id="element-7318" data-mp="1oj7ps5vsddu">
                A reason is required before this can be applied.
              </p>}
          </>}
      </Modal>
    </div>;
}
function AccountDetail({
  user,
  onAct
}: {
  user: AdminUser;
  onAct: (to: AccountState) => void;
}) {
  return <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-7319" data-mp="3a39za4mg9whm">
      <div className="space-y-5" data-id="element-7320" data-mp="3f11ogqvpn4i4">
        <Panel title="Account" bodyClassName="p-0" data-id="element-7321" data-mp="3f0tep7uvwfb5">
          <dl className="divide-y divide-line" data-id="element-7322" data-mp="3j3ycix40rstg">
            {[['Internal id', user.id], ['Handle', `@${user.username}`], ['Email', user.emailMasked], ['Roles held', user.roles.join(', ')], ['Campus', user.campus ?? 'Not set'], ['Created', user.createdAt], ['Last seen', user.lastSeen]].map(([label, value]) => <div key={label} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-5 py-3 sm:px-6" data-id="element-7323" data-mp="2qeig3uqlnawl">
                <dt className="text-body-sm text-ink-muted" data-id="element-7324" data-mp="2zfglagcrxogr">{label}</dt>
                <dd className="cz-num text-body text-ink" data-id="element-7325" data-mp="2bq44aub7gc3w">{value}</dd>
              </div>)}
          </dl>
          <p className="border-t border-line px-5 py-3 text-caption text-ink-subtle sm:px-6" data-id="element-7326" data-mp="27p2dn1vct8dj">
            The email is masked because an administrator needs to recognise an account, not to
            contact the person outside Campuzon.
          </p>
        </Panel>

        <Panel title="Standing history" bodyClassName="p-0" data-id="element-7327" data-mp="2gne1wx8qzu2t">
          {user.history.length === 0 ? <p className="px-5 py-4 text-body-sm text-ink-muted sm:px-6" data-id="element-7328" data-mp="1sxa10xmy28fi">
              No standing change has ever been applied to this account.
            </p> : <ul className="divide-y divide-line" data-id="element-7329" data-mp="3h7p68szcs8k">
              {user.history.map(entry => <li key={entry.id} className="px-5 py-4 sm:px-6" data-id="element-7330" data-mp="8ain1kftdyoc">
                  <p className="text-body font-semibold text-ink" data-id="element-7331" data-mp="3qo9oodwpkqts">{entry.action}</p>
                  <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-7332" data-mp="3vlrye6ijva2u">
                    {entry.actor} · {entry.at}
                  </p>
                  <p className="mt-1.5 max-w-[70ch] text-body-sm text-ink" data-id="element-7333" data-mp="n6zzyg3njld0">{entry.reason}</p>
                </li>)}
            </ul>}
        </Panel>

        <PermissionGate permission="users.identity.view" label="Identity evidence" data-id="element-7334" data-mp="d791k3bz0m7n">
          <Panel title="Identity evidence" description="Reviewer-only. The documents themselves are never rendered in a list view." bodyClassName="p-0" data-id="element-7335" data-mp="i6vo53dovet4">
            {user.identityDocuments.length === 0 ? <p className="px-5 py-4 text-body-sm text-ink-muted sm:px-6" data-id="element-7336" data-mp="32vjhsj3nwxqd">
                This account has submitted no identity evidence.
              </p> : <ul className="divide-y divide-line" data-id="element-7337" data-mp="3pkjbvp48yyqe">
                {user.identityDocuments.map(doc => <li key={doc.kind} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-5 py-3 sm:px-6" data-id="element-7338" data-mp="3kn9qqlnw598d">
                    <p className="text-body-sm text-ink" data-id="element-7339" data-mp="h5rjafc1648h">{doc.kind}</p>
                    <p className="text-body-sm text-ink-muted" data-id="element-7340" data-mp="c6y50kox6979">
                      {doc.submitted} · {doc.decision}
                    </p>
                  </li>)}
              </ul>}
          </Panel>
        </PermissionGate>
      </div>

      <div className="space-y-5" data-id="element-7341" data-mp="zwsecsa15fmr">
        <Panel title="Current standing" data-id="element-7342" data-mp="zx29svdugezu">
          <Badge tone={STATE_TONE[user.state]} data-id="element-7343" data-mp="zyvew68t6r5q">{STATE_LABEL[user.state]}</Badge>
          <p className="mt-2 max-w-[58ch] text-body-sm text-ink" data-id="element-7344" data-mp="v4iu9svlj9se">{STATE_MEANING[user.state]}</p>
          {user.stateReason && <p className="mt-3 rounded-md border border-line bg-canvas p-3 text-body-sm text-ink" data-id="element-7345" data-mp="13h2p1ineedst">
              {user.stateReason}
            </p>}
        </Panel>

        <PermissionGate permission="users.suspend" label="Change standing" data-id="element-7346" data-mp="1ce3777jvjfnt">
          <Panel title="Change standing" data-id="element-7347" data-mp="1hfiw5ap6a6bh">
            <ul className="space-y-3" data-id="element-7348" data-mp="1hc4q9shaveza">
              {ACTIONS.filter(action => action.to !== user.state).map(action => <li key={action.to} data-id="element-7349" data-mp="1qenqe4tpbdhu">
                  <Button variant="secondary" size="sm" onClick={() => onAct(action.to)} data-id="element-7350" data-mp="1qdmexz8rwozl">
                    {action.label}
                  </Button>
                  <p className="mt-1 max-w-[52ch] text-body-sm text-ink-muted" data-id="element-7351" data-mp="204pgt39j17zx">{action.blurb}</p>
                </li>)}
            </ul>
            <p className="mt-4 flex items-start gap-2 border-t border-line pt-3 text-caption text-ink-subtle" data-id="element-7352" data-mp="255mvv6z8plx7">
              <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-7353" data-mp="2a3em7wk8kzza" />
              Roles are granted and removed in Governance, not here. This changes standing only.
            </p>
          </Panel>
        </PermissionGate>

        <Panel title="Verification" data-id="element-7354" data-mp="3ik868z38da4j">
          <dl className="space-y-2.5" data-id="element-7355" data-mp="3ij5ih017fnsu">
            <div className="flex items-center justify-between gap-3" data-id="element-7356" data-mp="3imb6eby3kjmz">
              <dt className="text-body-sm text-ink-muted" data-id="element-7357" data-mp="3nkmh0ow7uy69">Email</dt>
              <dd data-id="element-7358" data-mp="3rlepvh16f120">
                <Badge tone={user.emailVerified ? 'ok' : 'warn'} data-id="element-7359" data-mp="3rjvbjvz2z02v">
                  {user.emailVerified ? 'Verified' : 'Not verified'}
                </Badge>
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3" data-id="element-7360" data-mp="56d883r4g7e4">
              <dt className="text-body-sm text-ink-muted" data-id="element-7361" data-mp="a4oiugp8qlxe">Academic</dt>
              <dd data-id="element-7362" data-mp="a5zkoaz4ljlv">
                <Badge plain data-id="element-7363" data-mp="f3hyaeo38n07">
                  {user.academicVerification === 'none' ? 'Not verified' : user.academicVerification === 'email' ? 'Institutional email' : user.academicVerification === 'document' ? 'Document reviewed' : 'Confirmed by institution'}
                </Badge>
              </dd>
            </div>
          </dl>
          <p className="mt-3 max-w-[58ch] border-t border-line pt-3 text-caption text-ink-subtle" data-id="element-7364" data-mp="1uoa5s946ljq7">
            Verification is evidence of an attribute. It is not a judgement about the person, and it
            does not decide standing.
          </p>
        </Panel>
      </div>
    </div>;
}